import React, { useContext, useState, useId } from "react";
import { OverlayContext } from "../../OverlaysContext";
import { DataContext } from "../../DataContext";
import { PropertyInput } from "../../components/PropertyInput";
import { PropertyDropdown } from "../../components/PropertyDropdown";
import { PropertyEditor } from "../../window/PropertyEditor";
import { ColorPicker } from "primereact/colorpicker";
import { Button } from "primereact/button";
import { PropertySwitch } from "../../components/PropetySwitch";
import { queryClient, useMutateGtfsResource, useQueryGtfsResource, useDeleteGtfsResource } from "../../Queries";
import { RouteTypeIndicator } from "../../components/RouteTypeIndicator";
import { PropertyCheckbox } from "../../components/PropertyCheckbox";
import { SnapshotContext } from "../../SnapshotContext";
import { ToolsContext, ToolsDispatchContext } from "../../ToolsContext";
import { routeTypeOptions, RouteTypePropertyDropdown } from "../../components/RouteTypePropertyDropdown";

export function RoutePropertyEditor({GRoute}) {
    const header = (
        <>
            <span className='text-200 text-sm'>{GRoute.route_id}</span> [{GRoute.route_short_name}] {GRoute.route_long_name}
        </>
    )

    return (
        <PropertyEditor resourceTable='routes' selectedItemId={GRoute.route_id} header={header}  >
            <RoutePropertiesForm></RoutePropertiesForm>
        </PropertyEditor>
    )
}

export function RoutePropertiesForm() {
    const id = useId();
    const snap = useContext(SnapshotContext);
    const data = useContext(DataContext);
    const handleOverlays = useContext(OverlayContext);
    const tool = useContext(ToolsContext);
    const dispatch = useContext(ToolsDispatchContext);
    const modifyRoute = useMutateGtfsResource();
    const deleteRoute = useDeleteGtfsResource('routes');
    const {data: networksData} = useQueryGtfsResource('networks');
    const [modifiedValues, setModifiedValues] = useState(data.inputValues);
    const [validationErrors, setValidationErrors] = useState(false);
    const [isMutationPending, setIsMutationPending] = useState(false);

    const networksOptions = [];
    if (networksData) {
        for(const [key,val] of  Object.entries(networksData)) {
            networksOptions.push({value: key, name: val});
        }
    }

    const symbolOptions = [
        {value: false,name: '(nie określono)'},
        {value: 'U',name: 'U – linia użyteczności publicznej'},
        {value: 'RS', name: 'RS – przewóz regularny specjalny'},
        {value: 'R', name: 'R – inny przewóz regularny'}
    ]

    
    function handleInput(propertyName,value) {
        let newValues = {...modifiedValues};
        newValues[propertyName] = value;
        setModifiedValues(newValues);
    }

    async function handleInputDone(propertyName, value) {
        if(value === data.inputValues[propertyName]) {
            return; // Nie wysyłamy zmian na serwer, jeżeli nowa wartość jest taka jak już zapisana do serwera.
        }

        const payload = {
            query: {
              modified_id: data.inputValues.route_id,
              resourceTable: 'routes'
            },
            fields: {}
          };
          payload.fields[propertyName] = value;
          setIsMutationPending(true);

          try {
            const asyncMutation = await modifyRoute.mutateAsync(payload);  // próbujemy przeprowadzić mutację

            // wykasuj ewentualne istniejące błędy walidacji z UI, skoro wszystko poszło ok
            if(validationErrors[propertyName]) {
                let updatedValidationErrors = {...validationErrors};
                updatedValidationErrors[propertyName] = false;
                setValidationErrors({
                    ...updatedValidationErrors
                });                   
            }
        } catch(error) {
            const errorCode = error.message.slice(0,3);
            if(errorCode == '422') {
                //obsługa błędów walidacji
                let updatedValidationErrors = {...validationErrors};
                updatedValidationErrors[propertyName] = Object.values(error.cause.validation[propertyName]).join(', ');
                setValidationErrors({
                    ...updatedValidationErrors
                });
            } else {
                //obsługa pozostałych błędów
                handleOverlays('toast',{
                    severity: "error",
                    summary: 'Błąd ' + errorCode,
                    detail: (typeof error.cause === 'undefined' || typeof error.cause.message === 'undefined') ? 'Wystąpił błąd. Czynność nie mogła zostać zakończona powodzeniem. ' + error.message : error.cause.message
                });
                console.log(error);
            }

        } finally {
            setIsMutationPending(false);
        }

    }

    function handleInputCancel() {
        setModifiedValues(data.inputValues);
    }

    function handleClickDelete() {
        async function accept() {
            try {
                //próbujemy usunąć linię
                const asyncMutation = await deleteRoute.mutateAsync(data.inputValues.route_id);
                // to wykona się tylko, jeżeli nam się udało
                dispatch({ // zamykamy edytor koniecznie
                    type: 'changed',
                    newTool: {
                        ...tool,
                        propertyEditor: null,  // wyczyść edytor (spowoduje jego ukrycie)
                        selection: null  // odznacz przystanek
                    }
                });
                handleOverlays('toast',{ summary: 'Linia została usunięta', severity: 'success'});
            } catch(error) { 
                // Obługa błędów
                console.log(error);
                const errorCode = error.message.slice(0,3);
                handleOverlays('toast',{
                    severity: "error",
                    summary: 'Błąd ' + errorCode,
                    detail: (typeof error.cause === 'undefined' || typeof error.cause.message === 'undefined') ? 'Wystąpił błąd. Czynność nie mogła zostać zakończona powodzeniem.' : error.cause.message 
                });               
            } finally {
                // musimy być pewni, że inwalidacja zapytań nastąpi PO zakończeniu operacji
                // dlatego następuje tutaj, a nie w Queries.js
                queryClient.invalidateQueries({queryKey: ['GTFS', snap, 'routes', data.inputValues.route_id]});
                queryClient.invalidateQueries({queryKey: ['GTFS', snap, 'routes']});
            }
        }

        handleOverlays('confirmDialog',{
            message: 'Linia zniknie z rozkładu jazdy wraz z wszystkimi trasami i kursami. Operacja jest nieodwracalna.',
            header: 'Usunąć linię ' + data.inputValues.route_id + ' ' + data.inputValues.route_short_name + ' ' + data.inputValues.route_long_name + '?',
            icon: 'pi pi-info-circle',
            style: { width: '500px '},
            breakpoints: { '960px': '100vw' },
            defaultFocus: 'reject',
            acceptLabel: 'Usuń',
            acceptClassName: 'p-button-danger',
            acceptIcon: 'fa-solid fa-trash',
            rejectLabel: 'Anuluj',
            accept
        })
    }

    return(
        <>
            <div className="formgrid grid">
                <div className="field col-6">
                    <PropertyInput
                        propertyName='route_id'
                        label="ID linii"
                        id={id}
                        value={modifiedValues.route_id}
                        initialVal={data.inputValues.route_id}
                        onDone={handleInputDone}
                        onChange={handleInput}
                        onCancel={handleInputCancel}
                        /* invalid={validationErrors.stop_id} */
                        disabled={false}
                        pending={isMutationPending}
                    />
                </div>
                <div className="field col-6">
                    <PropertyInput
                        propertyName='route_short_name'
                        label="Numer linii"
                        id={id}
                        value={modifiedValues.route_short_name}
                        initialVal={data.inputValues.route_short_name}
                        onDone={handleInputDone}
                        onChange={handleInput}
                        onCancel={handleInputCancel}
                        /* invalid={validationErrors.stop_id} */
                        disabled={false}
                        pending={isMutationPending}
                        style={{
                            backgroundColor: '#'+modifiedValues.route_color,
                            borderColor: '#'+modifiedValues.route_text_color,
                            color: '#'+modifiedValues.route_text_color,
                            fontWeight: 'bold'
                        }}
                    />
                </div>
                <div className="field col-12">
                    <PropertyInput
                        propertyName='route_long_name'
                        label="Nazwa linii"
                        id={id}
                        value={modifiedValues.route_long_name}
                        initialVal={data.inputValues.route_long_name}
                        onDone={handleInputDone}
                        onChange={handleInput}
                        onCancel={handleInputCancel}
                        /* invalid={validationErrors.stop_id} */
                        disabled={false}
                        pending={isMutationPending}
                    />
                </div>
                <div className="field col-6 text-right">
                    <label htmlFor={id+'_route_color'}>Kolor linii: </label> <ColorPicker 
                        inputId={id+'_route_color'} 
                        value={data.inputValues.route_color} 
                        defaultColor="000000"  
                        onChange={(e) => {
                            let newValues = {...modifiedValues};
                            newValues['route_color'] = e.value;
                            setModifiedValues(newValues);   // tzw. (trochę) optymistyczna aktualizacja...
                        }}
                        onHide={ () => { handleInputDone('route_color',modifiedValues.route_color)} }
                    />
                 </div>
                 <div className="field col-4">
                    <PropertyInput
                            propertyName='route_color'
                            id={id}
                            value={modifiedValues.route_color}
                            initialVal={data.inputValues.route_color}
                            onDone={handleInputDone}
                            onChange={handleInput}
                            onCancel={handleInputCancel}
                            /* invalid={validationErrors.stop_id} */
                            disabled={false}
                            pending={isMutationPending}
                    />
                 </div>
                <div className="field col-6 text-right">
                    <label htmlFor={id+'_route_text_color'}>Kolor numeru linii: </label> <ColorPicker  inputId={id+'_route_text_color'} value={data.inputValues.route_text_color} defaultColor="ffffff" 
                        onChange={(e) => {
                            let newValues = {...modifiedValues};
                            newValues['route_text_color'] = e.value;
                            setModifiedValues(newValues);   // tzw. (trochę) optymistyczna aktualizacja...
                        }}
                        onHide={ () => { handleInputDone('route_text_color',modifiedValues.route_text_color)} }
                    />
                </div>            
                <div className="field col-4">
                    <PropertyInput
                            propertyName='route_text_color'
                            id={id}
                            value={modifiedValues.route_text_color}
                            initialVal={data.inputValues.route_text_color}
                            onDone={handleInputDone}
                            onChange={handleInput}
                            onCancel={handleInputCancel}
                            /* invalid={validationErrors.stop_id} */
                            disabled={false}
                            pending={isMutationPending}
                    />
                </div>
                <div className='field col-12'>
                    <RouteTypePropertyDropdown 
                         propertyName='route_type' 
                         initialVal={data.inputValues.route_type} 
                         label="Trakcja" 
                         onDone={handleInputDone} 
                    />
                </div>
                <div className="field col-4">
                    <PropertyInput
                        propertyName='route_sort_order'
                        label="Pozycja w spisie"
                        id={id}
                        value={modifiedValues.route_sort_order}
                        initialVal={data.inputValues.route_sort_order}
                        onDone={handleInputDone}
                        onChange={handleInput}
                        onCancel={handleInputCancel}
                        /* invalid={validationErrors.stop_id} */
                        disabled={false}
                        pending={isMutationPending}
                    />
                </div>
                <div className="field col-8">
                    <PropertyInput
                        propertyName='route_url'
                        label="Link do WWW z opisem linii"
                        placeholder="(np. https://example.com/trasa)"
                        id={id}
                        value={modifiedValues.route_url}
                        initialVal={data.inputValues.route_url}
                        onDone={handleInputDone}
                        onChange={handleInput}
                        onCancel={handleInputCancel}
                        /* invalid={validationErrors.stop_id} */
                        disabled={false}
                        pending={isMutationPending}
                    />
                </div>
                <div className='field col-12'>
                    <PropertyDropdown 
                        propertyName='network_id' 
                        initialVal={data.inputValues.network_id} 
                        label="Sieć komunikacyjna" 
                        options={networksOptions} 
                        onDone={handleInputDone} 
                    />
                </div>
                <div className='field col-8'>
                    <PropertyDropdown 
                        propertyName='route_symbol' 
                        initialVal={data.inputValues.route_symbol} 
                        label="Oznaczenie linii" 
                        options={symbolOptions} 
                        onDone={handleInputDone} 
                    />
                </div>
                <div className='field col-4'>
                    <PropertySwitch 
                        propertyName='suspended'
                        initialVal={data.inputValues.suspended}
                        label='Zawieszona'
                        id={id}
                        onDone={handleInputDone}
                    />
                </div>
                <div className="field col-6">
                    <PropertyCheckbox
                        propertyName='wheelchair_accessible'
                        label='Dostępna dla wózków'
                        initialVal={data.inputValues.wheelchair_accessible}
                        id={id}
                        onDone={handleInputDone}
                    />
                </div>
                <div className="field col-6">
                    <PropertyCheckbox
                        propertyName='air_conditioned'
                        label='Klimatyzowana'
                        initialVal={data.inputValues.air_conditioned}
                        id={id}
                        onDone={handleInputDone}
                    />
                </div>
                <div className="field col-12 mt-3">
                    <Button severity="danger" onClick={handleClickDelete} icon="fa-solid fa-trash" label="Usuń linię" outlined></Button>
                </div>
            </div>

        </>
    );
}